const NOTE_TEXTAREA_SELECTOR = 'textarea[data-u-comp="note-textarea"]'
const NOTE_RESOURCE_NAME = 'SHEET_NOTE_PLUGIN'
export const UNIVER_NOTE_SIZE_CHANGED_EVENT = 'univer-note-size-changed'
const MIN_WIDTH = 160
const MAX_WIDTH = 460
const MIN_HEIGHT = 72
const MAX_HEIGHT = 360
const EXTRA_WIDTH = 32

let observer: MutationObserver | null = null
let measureCanvas: HTMLCanvasElement | null = null
const boundTextareas = new WeakSet<HTMLTextAreaElement>()
const manualResizeState = new WeakMap<
  HTMLTextAreaElement,
  { pointerWidth: number; pointerHeight: number; manuallyResized: boolean; userInputStarted: boolean }
>()

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value))

const getTextWidth = (textarea: HTMLTextAreaElement, text: string) => {
  if (!measureCanvas) {
    measureCanvas = document.createElement('canvas')
  }

  const context = measureCanvas.getContext('2d')
  if (!context) return text.length * 8

  const style = window.getComputedStyle(textarea)
  context.font = `${style.fontStyle} ${style.fontVariant} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
  return context.measureText(text || ' ').width
}

const getPreferredWidth = (textarea: HTMLTextAreaElement) => {
  const value = textarea.value || textarea.placeholder || ''
  const lines = value.split(/\r?\n/)
  const widestLine = lines.reduce((max, line) => Math.max(max, getTextWidth(textarea, line)), 0)
  const viewportLimit = Math.max(MIN_WIDTH, window.innerWidth - 64)

  return Math.round(clamp(widestLine + EXTRA_WIDTH, MIN_WIDTH, Math.min(MAX_WIDTH, viewportLimit)))
}

const getPreferredHeight = (textarea: HTMLTextAreaElement) => {
  const viewportLimit = Math.max(MIN_HEIGHT, window.innerHeight - 96)
  return Math.round(clamp(textarea.scrollHeight + 2, MIN_HEIGHT, Math.min(MAX_HEIGHT, viewportLimit)))
}

const resizeNoteTextarea = (textarea: HTMLTextAreaElement) => {
  if (!textarea.isConnected) return
  const state = manualResizeState.get(textarea)

  if (state?.manuallyResized) {
    textarea.style.overflow = 'auto'
    return
  }

  const width = getPreferredWidth(textarea)
  textarea.style.width = `${width}px`
  textarea.style.height = 'auto'

  const height = getPreferredHeight(textarea)
  textarea.style.height = `${height}px`
  textarea.style.overflow = textarea.scrollHeight > height || textarea.scrollWidth > width ? 'auto' : 'hidden'
}

const scheduleResize = (textarea: HTMLTextAreaElement) => {
  window.requestAnimationFrame(() => resizeNoteTextarea(textarea))
}

const bindTextarea = (textarea: HTMLTextAreaElement) => {
  if (boundTextareas.has(textarea)) return

  const initialWidth = textarea.offsetWidth
  const initialHeight = textarea.offsetHeight
  const hasExistingNoteText = textarea.value.trim().length > 0

  boundTextareas.add(textarea)
  manualResizeState.set(textarea, {
    pointerWidth: initialWidth,
    pointerHeight: initialHeight,
    manuallyResized: hasExistingNoteText,
    userInputStarted: false,
  })

  const markManualResizeIfNeeded = () => {
    const state = manualResizeState.get(textarea)
    if (!state) return

    const widthChanged = Math.abs(textarea.offsetWidth - state.pointerWidth) > 2
    const heightChanged = Math.abs(textarea.offsetHeight - state.pointerHeight) > 2
    if (widthChanged || heightChanged) {
      state.manuallyResized = true
      textarea.style.overflow = 'auto'
      window.dispatchEvent(new CustomEvent(UNIVER_NOTE_SIZE_CHANGED_EVENT))
    }
  }

  textarea.addEventListener('pointerdown', () => {
    const state = manualResizeState.get(textarea)
    if (!state) return

    state.pointerWidth = textarea.offsetWidth
    state.pointerHeight = textarea.offsetHeight
    window.addEventListener('pointerup', markManualResizeIfNeeded, { once: true })
  })

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      const state = manualResizeState.get(textarea)
      if (!state || state.userInputStarted || state.manuallyResized) return
      if (textarea.value.trim().length > 0) {
        state.manuallyResized = true
        textarea.style.overflow = 'auto'
      }
    })
  })

  textarea.addEventListener('input', () => {
    const state = manualResizeState.get(textarea)
    if (state) state.userInputStarted = true
    scheduleResize(textarea)
  })
  if (hasExistingNoteText) {
    textarea.style.overflow = 'auto'
  }
}

const bindExistingTextareas = () => {
  document
    .querySelectorAll<HTMLTextAreaElement>(NOTE_TEXTAREA_SELECTOR)
    .forEach(bindTextarea)
}

export const installUniverNoteAutosize = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined' || observer) return

  bindExistingTextareas()
  observer = new MutationObserver(() => bindExistingTextareas())
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  })

  window.addEventListener('resize', bindExistingTextareas)
}

export const syncOpenUniverNoteSizeToSnapshot = <T extends Record<string, any> | null | undefined>(
  snapshot: T
): T => {
  if (!snapshot || typeof document === 'undefined') return snapshot

  const textareas = Array.from(document.querySelectorAll<HTMLTextAreaElement>(NOTE_TEXTAREA_SELECTOR))
    .filter((textarea) => textarea.isConnected)
  if (textareas.length === 0) return snapshot

  const resources = Array.isArray((snapshot as any).resources) ? (snapshot as any).resources : []
  const noteResource = resources.find((resource: any) => resource?.name === NOTE_RESOURCE_NAME)
  if (!noteResource || typeof noteResource.data !== 'string') return snapshot

  let noteData: Record<string, any>
  try {
    noteData = JSON.parse(noteResource.data || '{}')
  } catch {
    return snapshot
  }

  let changed = false
  textareas.forEach((textarea) => {
    const noteText = textarea.value
    const width = textarea.getBoundingClientRect().width
    const height = textarea.getBoundingClientRect().height
    if (!noteText || width <= 0 || height <= 0) return

    Object.values(noteData).forEach((sheetNotes: any) => {
      Object.values(sheetNotes || {}).forEach((rowNotes: any) => {
        Object.values(rowNotes || {}).forEach((note: any) => {
          if (!note || note.note !== noteText) return

          note.width = width
          note.height = height
          changed = true
        })
      })
    })
  })

  if (changed) {
    noteResource.data = JSON.stringify(noteData)
  }

  return snapshot
}
